// Vendor
import * as React from 'react';

// Bernie
import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ClickTracker } from 'bernie-analytics';

// Models
import { ListingFooterViewModel } from 'models';

// UITK
import { UitkIcon } from 'uitk-react-icons';
import { classNames } from 'uitk-react-utils';

// Components
import { DoubleLink } from 'components/common/media/double-link';
import { ListingComponentProps } from 'components/search/listing';
import { MessagingCardActions } from 'components/common/messaging-card/messaging-card-actions';
import { TargetableLink } from 'components/common/targetable-link';

const DoubleLinkTracked = ClickTracker(DoubleLink);

export interface FooterProps extends ListingComponentProps {
  listingClickHandler?: () => void;
  listingFooter?: ListingFooterViewModel;
}

export const ListingFooter: React.FunctionComponent<FooterProps> = ({
  analytics,
  listingFooter,
  routeStore,
  context,
  model,
  dataAttributes,
  listingClickHandler,
  referralObject
}) => {
  if (!model || !listingFooter) {
    return null;
  }

  const { hasAvailableOffer, infositeUrl, name, travelAd } = model;
  const { actionModel, actionType, icon, linkName } = listingFooter;

  if (!hasAvailableOffer || !(actionModel && actionModel.text)) {
    return null;
  }

  const { href, rfrr, text } = actionModel;

  const footerClassNames = ['uitk-cell', 'uitk-card-separator-top', 'all-x-padding-three', 'all-y-padding-two'];

  const listingDataAttributes = dataAttributes ? dataAttributes.listing : {};
  const analyticHotelOpenProps = referralObject ? referralObject.navigation : {};

  const hotelLinkProps = {
    name,
    rfrr,
    'data-stid': 'listing-footer-open-infosite',
    linkName: 'View selected hotel',
    to: infositeUrl,
    onClick: listingClickHandler,
    ...analyticHotelOpenProps,
    ...listingDataAttributes
  };

  const signInLinkProps = {
    'data-stid': 'listing-footer-sign-in',
    linkName,
    onClick: MessagingCardActions.resolveAction(routeStore, actionModel, analytics),
    rfrr,
    to: href,
    trackImmediate: true
  };

  let doubleLinkProps;
  let shouldLinkTarget = false;

  switch (actionType) {
    case 'PINNED_PROPERTY_HOTEL_LINK':
      doubleLinkProps = hotelLinkProps;

      if (!(travelAd && travelAd.clickRedirectLinkoffTrackingUrl)) {
        shouldLinkTarget = true;
      }

      break;
    case 'SIGN_IN_LINK':
      doubleLinkProps = signInLinkProps;

      break;
    default:
      return null;
  }

  const isMobile = context && context.deviceInformation && context.deviceInformation.mobile;

  const innerClasses = classNames(
    ['uitk-flex', 'uitk-flex-align-items-center', 'uitk-type-medium', 'pwa-theme--link', 'uitk-flex-row-reverse'],
    { 'uitk-flex-justify-content-space-between': isMobile }
  );

  return (
    <>
      <AbacusExperiment name="pwa_hsr_pinned_property">
        <AbacusControl />
        <AbacusVariant bucket={1} />
      </AbacusExperiment>
      <footer className={`${footerClassNames.join(' ')}`}>
        <div className={innerClasses}>
          <IconWrapper icon={icon} shouldLinkTarget={shouldLinkTarget} doubleLinkProps={doubleLinkProps} />
          <TextWrapper text={text} shouldLinkTarget={shouldLinkTarget} doubleLinkProps={doubleLinkProps} />
        </div>
      </footer>
    </>
  );
};

interface IconWrapperModel {
  icon?: string;
  shouldLinkTarget?: boolean;
  doubleLinkProps?: object;
}
const IconWrapper = ({ icon, shouldLinkTarget, doubleLinkProps }: IconWrapperModel) => {
  if (!icon) {
    return null;
  }

  if (shouldLinkTarget) {
    return (
      <TargetableLink>
        <IconDoubleLink icon={icon} doubleLinkProps={doubleLinkProps} />
      </TargetableLink>
    );
  }

  return <IconDoubleLink icon={icon} doubleLinkProps={doubleLinkProps} />;
};

interface IconDoubleLinkModel {
  icon?: string;
  shouldLinkTarget?: boolean;
  doubleLinkProps?: object;
}
const IconDoubleLink = ({ icon, doubleLinkProps }: IconDoubleLinkModel) => (
  <DoubleLinkTracked {...doubleLinkProps}>
    <div>
      <UitkIcon name={icon} />
    </div>
  </DoubleLinkTracked>
);

interface TextWrapperModel {
  text?: string;
  shouldLinkTarget?: boolean;
  doubleLinkProps?: object;
}
const TextWrapper = ({ text, shouldLinkTarget, doubleLinkProps }: TextWrapperModel) => {
  if (shouldLinkTarget) {
    return (
      <TargetableLink>
        <TextDoubleLink text={text} doubleLinkProps={doubleLinkProps} />
      </TargetableLink>
    );
  }

  return <TextDoubleLink text={text} doubleLinkProps={doubleLinkProps} />;
};

interface TextDoubleLinkModel {
  text?: string;
  doubleLinkProps?: object;
}
const TextDoubleLink = ({ text, doubleLinkProps }: TextDoubleLinkModel) => (
  <DoubleLinkTracked {...doubleLinkProps}>{text}</DoubleLinkTracked>
);
